import { FlightIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import PageLoader from '@components/PageLoader'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useAuth } from '@context/AuthContext'
import { useFlights, useTrip, useTripMembers } from '@hooks'
import { GetFlightGroupResponse, GetUserType } from '@orbit/api-types'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AddFlightModal from './AddFlightModal'
import {
	getArrivalDisplay,
	getDepartureDisplay,
	getFlightCode,
	sortGroupsByDeparture,
} from './flightHelpers'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import {
	AddButton,
	AvatarStack,
	FlightGroupCard,
	FlightGroupGrid,
	FlightGroupHeader,
	FlightGroupTitle,
	LoadingContainer,
	ManualPill,
	StackedAvatar,
	PassengerCount,
	PassengerRow,
	RouteAirport,
	RouteArrow,
	RouteBlock,
	RouteEndpoint,
	RouteLabel,
	RouteTime,
	YoureAPassengerPill,
} from './FlightsPage.styles'
import { iconSizes } from '@/theme/tokens'
import { ErrorNote } from '@components/Notice'

const MAX_VISIBLE_AVATARS = 4


const getGroupPassengerIds = (group: GetFlightGroupResponse): number[] => {
	const ids = new Set<number>()
	for (const leg of group.flights) {
		for (const p of leg.passengers) {
			ids.add(p.userId)
		}
	}
	return Array.from(ids)
}

const FlightsPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { user } = useAuth()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: flightsData, isLoading: flightsLoading, error: flightsError } = useFlights(tripId)
	const { data: membersData } = useTripMembers(tripId)

	const [isAddModalOpen, setIsAddModalOpen] = useState(false)

	const members = membersData?.members ?? []
	const flightGroups = flightsData?.flightGroups ?? []

	useBreadcrumbs(
		trip
			? [
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Flights' },
				]
			: []
	)

	const getMemberById = (userId: number): GetUserType | undefined => {
		return members.find(m => m.id === userId)
	}

	const sortedGroups = sortGroupsByDeparture(flightGroups)

	if (tripLoading || flightsLoading) {
		return (
			<PageLoader label="Loading flights..." />
		)
	}

	if (tripError || flightsError) {
		return (
			<ErrorNote>
				Error loading flights: {tripError?.message || flightsError?.message}
			</ErrorNote>
		)
	}

	if (!trip) {
		return <LoadingContainer>Trip not found</LoadingContainer>
	}

	return (
		<PageScroll>
			{flightGroups.length === 0 ? (
				<EmptyState
					art="flights"
					title="No flights yet"
					description="Flights you add to this trip will show up here."
				>
					<EmptyStateButton onClick={() => setIsAddModalOpen(true)}>Add Flight</EmptyStateButton>
				</EmptyState>
			) : (
				<PageColumn>
					<PageHeader
						title="Flights"
						subtitle={countLabel(flightGroups.length, 'flight')}
						actions={
							<AddButton onClick={() => setIsAddModalOpen(true)}>
								+ Add Flight
							</AddButton>
						}
					/>

					<FlightGroupGrid>
						{sortedGroups.map(group => {
							const passengerIds = getGroupPassengerIds(group)
							const visibleIds = passengerIds.slice(0, MAX_VISIBLE_AVATARS)
							const overflowCount = passengerIds.length - MAX_VISIBLE_AVATARS
							const isUserPassenger = user ? passengerIds.includes(user.id) : false

							const firstLeg = group.flights[0]
							const lastLeg = group.flights[group.flights.length - 1]
							const hasManual = group.flights.some(l => l.isManuallyAdded)

							const titleCode = firstLeg ? getFlightCode(firstLeg) : 'Flight'
							const depCode = firstLeg?.departureAirportCode ?? firstLeg?.departureAirport?.iataCode ?? 'N/A'
							const arrCode = lastLeg?.arrivalAirportCode ?? lastLeg?.arrivalAirport?.iataCode ?? 'N/A'
							const depStr = firstLeg ? getDepartureDisplay(firstLeg) : null
							const arrStr = lastLeg ? getArrivalDisplay(lastLeg) : null

							return (
								<FlightGroupCard
									key={group.id}
									onClick={() => navigate(`/trips/${tripId}/flights/${group.id}`)}
								>
									<FlightGroupHeader>
										<FlightGroupTitle>{titleCode}</FlightGroupTitle>
										{hasManual && <ManualPill>Manual</ManualPill>}
									</FlightGroupHeader>

									<RouteBlock>
										<RouteEndpoint $align="start">
											<RouteAirport>{depCode}</RouteAirport>
											<RouteLabel>Departs</RouteLabel>
											<RouteTime>{depStr ?? 'N/A'}</RouteTime>
										</RouteEndpoint>
										<RouteArrow>
											<FlightIcon sx={{ fontSize: iconSizes.xs }} />
										</RouteArrow>
										<RouteEndpoint $align="end">
											<RouteAirport>{arrCode}</RouteAirport>
											<RouteLabel>Arrives</RouteLabel>
											<RouteTime>{arrStr ?? 'N/A'}</RouteTime>
										</RouteEndpoint>
									</RouteBlock>

									<PassengerRow>
										<AvatarStack>
											{visibleIds.map((userId, index) => {
												const member = getMemberById(userId)
												if (!member) return null
												return (
													<StackedAvatar key={userId} $index={index} user={member} size="sm" ring />
												)
											})}
											{overflowCount > 0 && (
												<StackedAvatar $index={MAX_VISIBLE_AVATARS} size="sm" ring tone="gold" label={`+${overflowCount}`} />
											)}
										</AvatarStack>
										<PassengerCount>
											{countLabel(passengerIds.length, 'passenger')}
										</PassengerCount>
										{isUserPassenger && (
											<YoureAPassengerPill>You're a passenger</YoureAPassengerPill>
										)}
									</PassengerRow>
								</FlightGroupCard>
							)
						})}
					</FlightGroupGrid>
				</PageColumn>
			)}

			<AddFlightModal
				isOpen={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				members={members}
			/>
		</PageScroll>
	)
}

export default FlightsPage
